import React, { useState, useEffect, useCallback } from "react";
import { Box, Typography, Card, CardContent, Grid, useTheme, Chip, Divider, Skeleton, Button } from "@mui/material";
import axios from "axios";
import { API_URL } from "../../api-config";
import { tokens } from "../../theme";
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import RefreshIcon from '@mui/icons-material/Refresh';

const SmartBands = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const [lastValue, setLastValue] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);

    const fetchData = useCallback(async (isRefresh = false) => {
        if (isRefresh) setRefreshing(true);
        else setLoading(true);

        try {
            const token = localStorage.getItem("token");
            const response = await axios.get(`${API_URL}/trading-api/toolbox/avg_price_2/?limit=1`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.data && response.data.length > 0) {
                setLastValue(response.data[0]);
            } else {
                setLastValue(null);
            }
        } catch (error) {
            console.error("Error fetching Smart Bands data:", error);
            setLastValue(null);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const getRegimeColor = (regime) => {
        switch (regime) {
            case "Alta": return colors.redAccent[500];
            case "Media": return colors.blueAccent[500];
            case "Baja": return colors.greenAccent[500];
            default: return colors.grey[100];
        }
    };

    const getFormatDate = (dateStr) => {
        const date = new Date(dateStr);
        return date.toLocaleString();
    };

    return (
        <Box m="20px">
            <Box display="flex" justifyContent="space-between" alignItems="center" mb="30px">
                <Typography variant="h3" fontWeight="bold" color={colors.grey[100]}>
                    Smart Bands Projections
                </Typography>
                <Button
                    variant="contained"
                    onClick={() => fetchData(true)}
                    disabled={refreshing}
                    startIcon={<RefreshIcon />}
                    sx={{
                        backgroundColor: colors.blueAccent[700],
                        color: colors.grey[100],
                        fontSize: "14px",
                        fontWeight: "bold",
                        padding: "10px 20px",
                        "&:hover": {
                            backgroundColor: colors.blueAccent[800],
                        }
                    }}
                >
                    {refreshing ? "Refreshing..." : "Refresh Data"}
                </Button>
            </Box>

            {loading ? (
                <Box display="flex" justifyContent="center">
                    <Skeleton variant="rectangular" width="100%" maxWidth="600px" height={350} sx={{ borderRadius: "8px" }} />
                </Box>
            ) : lastValue ? (
                <Box display="flex" justifyContent="center">
                    <Card sx={{
                        backgroundColor: colors.primary[400],
                        borderRadius: "12px",
                        boxShadow: "0 8px 12px rgba(0,0,0,0.5)",
                        border: `1px solid ${colors.grey[700]}`,
                        width: "100%",
                        maxWidth: "600px"
                    }}>
                        <CardContent sx={{ p: "30px" }}>
                            <Box display="flex" justifyContent="space-between" mb="15px">
                                <Typography variant="h5" color={colors.greenAccent[400]} display="flex" alignItems="center" fontWeight="bold">
                                    <AccessTimeIcon sx={{ mr: 1, fontSize: "1.2rem" }} /> {getFormatDate(lastValue.fecha)}
                                </Typography>
                                <Chip
                                    label={lastValue.modulo}
                                    sx={{ backgroundColor: colors.blueAccent[700], color: "white", fontWeight: "bold", fontSize: "14px" }}
                                />
                            </Box>

                            <Box mb="25px" textAlign="center">
                                <Typography variant="h2" fontWeight="700" color={colors.grey[100]} sx={{ mb: 1 }}>
                                    VWAP: {parseFloat(lastValue.value).toFixed(2)}
                                </Typography>
                                <Box display="flex" justifyContent="center" gap="15px" mb="10px">
                                    <Chip
                                        label={`ATR(14): ${parseFloat(lastValue.atr_14).toFixed(2)}`}
                                        variant="outlined"
                                        color="secondary"
                                        sx={{ fontWeight: "bold" }}
                                    />
                                    <Chip
                                        label={`ATR(5): ${parseFloat(lastValue.atr_5).toFixed(2)}`}
                                        variant="outlined"
                                        color="secondary"
                                        sx={{ fontWeight: "bold" }}
                                    />
                                </Box>
                                <Box display="flex" justifyContent="center" gap="15px">
                                    <Chip
                                        label={`Regime: ${lastValue.regime}`}
                                        variant="outlined"
                                        sx={{ borderColor: getRegimeColor(lastValue.regime), color: getRegimeColor(lastValue.regime), fontWeight: "bold" }}
                                    />
                                    <Chip
                                        label={`State: ${lastValue.volatility_state}`}
                                        variant="outlined"
                                        sx={{ borderColor: colors.grey[100], color: colors.grey[100] }}
                                    />
                                </Box>
                            </Box>

                            <Divider sx={{ mb: "25px", opacity: 0.2 }} />

                            <Grid container spacing={4}>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="h6" color={colors.redAccent[400]} sx={{ mb: 2, fontWeight: "bold", borderBottom: `2px solid ${colors.redAccent[700]}`, pb: 0.5 }}>
                                        SELL BANDS
                                    </Typography>
                                    <Box display="flex" flexDirection="column" gap="10px">
                                        <Box display="flex" justifyContent="space-between">
                                            <Typography variant="body1" color={colors.grey[300]}>0.5 Base:</Typography>
                                            <Typography variant="body1" fontWeight="bold">{parseFloat(lastValue.up05).toFixed(2)}</Typography>
                                        </Box>
                                        <Box display="flex" justifyContent="space-between">
                                            <Typography variant="body1" color={colors.grey[300]}>0.75 Alerta:</Typography>
                                            <Typography variant="body1" fontWeight="bold">{parseFloat(lastValue.up075).toFixed(2)}</Typography>
                                        </Box>
                                        <Box display="flex" justifyContent="space-between">
                                            <Typography variant="body1" color={colors.grey[300]}>0.9 Extrema:</Typography>
                                            <Typography variant="body1" fontWeight="bold">{parseFloat(lastValue.up09).toFixed(2)}</Typography>
                                        </Box>
                                    </Box>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="h6" color={colors.greenAccent[400]} sx={{ mb: 2, fontWeight: "bold", borderBottom: `2px solid ${colors.greenAccent[700]}`, pb: 0.5 }}>
                                        BUY BANDS
                                    </Typography>
                                    <Box display="flex" flexDirection="column" gap="10px">
                                        <Box display="flex" justifyContent="space-between">
                                            <Typography variant="body1" color={colors.grey[300]}>0.5 Base:</Typography>
                                            <Typography variant="body1" fontWeight="bold">{parseFloat(lastValue.down05).toFixed(2)}</Typography>
                                        </Box>
                                        <Box display="flex" justifyContent="space-between">
                                            <Typography variant="body1" color={colors.grey[300]}>0.75 Alerta:</Typography>
                                            <Typography variant="body1" fontWeight="bold">{parseFloat(lastValue.down075).toFixed(2)}</Typography>
                                        </Box>
                                        <Box display="flex" justifyContent="space-between">
                                            <Typography variant="body1" color={colors.grey[300]}>0.9 Extrema:</Typography>
                                            <Typography variant="body1" fontWeight="bold">{parseFloat(lastValue.down09).toFixed(2)}</Typography>
                                        </Box>
                                    </Box>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Box>
            ) : (
                <Typography variant="h5" color="error" textAlign="center">No data found</Typography>
            )}
        </Box>
    );
};

export default SmartBands;
